import { useCallback, useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { MessagesSquare, Paperclip, Send, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import * as api from "@/lib/api";
import VoiceRecorder from "@/components/lab/VoiceRecorder";
import ChatQueue from "@/components/lab/ChatQueue";
import ChatHistory from "@/components/lab/ChatHistory";

const phoneOf = (p) => (p.anamnesi?.generali?.cellulare || "").replace(/[^\d]/g, "");
const nameOf = (p) => [p.cognome, p.nome].filter(Boolean).join(" ") || "Senza nome";

function Recipients({ patients, sel, setSel }) {
  const [q, setQ] = useState("");
  const withPhone = patients.filter(phoneOf);
  const all = withPhone.length > 0 && withPhone.every((p) => sel.includes(p.id));
  const list = patients.filter((p) => nameOf(p).toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input className="lab-input flex-1 py-1.5 text-xs" placeholder="Cerca paziente…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="chat-search" />
        <button onClick={() => setSel(all ? [] : withPhone.map((p) => p.id))} className={`rounded-lg px-3 py-1.5 text-xs font-bold ${all ? "bg-[hsl(199_89%_55%)] text-black" : "border border-white/10 hover:bg-white/5"}`} data-testid="chat-select-all">ALL</button>
      </div>
      <div className="max-h-52 space-y-0.5 overflow-y-auto rounded-lg border border-white/5 p-1" data-testid="chat-patient-list">
        {list.map((p) => {
          const ok = !!phoneOf(p);
          return (
            <label key={p.id} className={`flex items-center gap-2 rounded px-2 py-1.5 text-xs ${ok ? "cursor-pointer hover:bg-white/5" : "opacity-50"}`}>
              <input type="checkbox" disabled={!ok} checked={sel.includes(p.id)} onChange={(e) => setSel((s) => (e.target.checked ? [...s, p.id] : s.filter((x) => x !== p.id)))} data-testid={`chat-check-${p.id}`} />
              <span className="flex-1 truncate">{nameOf(p)}</span>
              {!ok && <span className="text-[10px] italic">senza telefono</span>}
            </label>
          );
        })}
      </div>
    </div>
  );
}

function Composer({ patients, onReady }) {
  const [sel, setSel] = useState([]);
  const [text, setText] = useState("");
  const [voice, setVoice] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const b = await api.createBroadcast({ patient_ids: sel, text, has_voice: !!voice, file_name: file?.name || "" });
      onReady(b, [voice, file].filter(Boolean));
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nella preparazione"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <Recipients patients={patients} sel={sel} setSel={setSel} />
      <textarea className="lab-input min-h-[100px] text-sm" placeholder="Scrivi il messaggio…" value={text} onChange={(e) => setText(e.target.value)} maxLength={4000} data-testid="chat-text" />
      <VoiceRecorder voice={voice} setVoice={setVoice} onRecording={setRecording} />
      {file ? (
        <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/25 px-3 py-2 text-xs" data-testid="chat-file-ready"><Paperclip className="h-3.5 w-3.5" /><span className="flex-1 truncate">{file.name}</span><button onClick={() => setFile(null)} data-testid="chat-file-remove"><X className="h-3.5 w-3.5" /></button></div>
      ) : (
        <label className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-sm font-medium hover:bg-white/5"><Paperclip className="h-4 w-4" /> Allega file<input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="chat-file-input" /></label>
      )}
      <button onClick={submit} disabled={busy || recording || !sel.length || !(text.trim() || voice || file)} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[hsl(199_89%_55%)] px-3 py-3 text-sm font-bold text-black disabled:opacity-40" data-testid="chat-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Invia a {sel.length} {sel.length === 1 ? "paziente" : "pazienti"} su Telegram
      </button>
    </div>
  );
}

export default function ChatDrawer({ open, setOpen, patients = [] }) {
  const [view, setView] = useState("new");
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const load = useCallback(async () => { try { setHistory(await api.listBroadcasts()); } catch (e) { console.error(e); } }, []);
  useEffect(() => { if (open) load(); }, [open, load]);
  const del = async (b) => { if (!window.confirm("Eliminare questo messaggio dallo storico?")) return; try { await api.deleteBroadcast(b.id); load(); } catch (e) { toast.error("Errore"); } };
  const tab = (id, label) => <button onClick={() => { setView(id); if (id === "history") load(); }} data-active={view === id} className="tab-pill flex-1" data-testid={`chat-tab-${id}`}>{label}</button>;
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full border-white/10 bg-[hsl(var(--card))] text-foreground sm:max-w-md" data-testid="chat-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 font-display text-foreground"><MessagesSquare className="h-5 w-5 text-[hsl(199_89%_60%)]" /> Chat Telegram</SheetTitle>
          <SheetDescription className="sr-only">Invia un messaggio ai pazienti su Telegram</SheetDescription>
        </SheetHeader>
        <div className="mt-4 flex gap-1 rounded-xl border border-white/5 bg-black/20 p-1">{tab("new", "Nuovo messaggio")}{tab("history", "Storico")}</div>
        <div className="mt-4 overflow-y-auto pb-6 pr-1" style={{ maxHeight: "calc(100vh - 160px)" }}>
          {view === "history" ? <ChatHistory items={history} onDelete={del} />
            : active ? <ChatQueue broadcast={active.b} files={active.files} onClose={() => { setActive(null); load(); }} />
            : <Composer patients={patients} onReady={(b, files) => setActive({ b, files })} />}
        </div>
      </SheetContent>
    </Sheet>
  );
}
